import { useEffect, useMemo } from 'react'
import { create } from 'zustand'
import { getDashboardStats, type DashboardStatsPayload } from '../api/dashboard'
import { LISTENER_COUNT_REFRESH_INTERVAL_MS } from '../utils/listenerCount'
import useUnreadStore from './unread'

type DashboardStatsData = {
  unreadTotal: number | null
  messagesToday: number | null
  newPrepItemsLast24Hours: number | null
  myPrepCount: number | null
  loading: boolean
  error: string | null
  lastUpdated: number | null
  nextRefreshAt: number | null
}

type DashboardStatsStore = DashboardStatsData & {
  fetch: (
    stationId: number,
    options?: { signal?: AbortSignal },
  ) => Promise<DashboardStatsPayload | null>
  setNextRefreshAt: (timestamp: number | null) => void
  reset: () => void
}

const DEFAULT_ERROR_MESSAGE = 'Unable to load dashboard stats.'

const createInitialState = (): DashboardStatsData => ({
  unreadTotal: null,
  messagesToday: null,
  newPrepItemsLast24Hours: null,
  myPrepCount: null,
  loading: false,
  error: null,
  lastUpdated: null,
  nextRefreshAt: null,
})

const sanitizeCount = (value: unknown): number => {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) {
    return 0
  }

  const normalized = Math.floor(numeric)
  return normalized >= 0 ? normalized : 0
}

const useDashboardStatsStore = create<DashboardStatsStore>((set) => ({
  ...createInitialState(),
  async fetch(stationId, options = {}) {
    if (!Number.isFinite(stationId) || stationId <= 0) {
      set(createInitialState())
      return null
    }

    set({ loading: true, error: null, nextRefreshAt: null })

    try {
      const response = await getDashboardStats(stationId, options)
      const unread = sanitizeCount(response.unreadTotal)
      const messagesToday = sanitizeCount(response.messagesToday)
      const newPrepItems = sanitizeCount(response.newPrepItemsLast24Hours)
      const myPrep = sanitizeCount(response.myPrepCount)

      useUnreadStore.getState().setTotal(unread)

      const now = Date.now()

      set({
        unreadTotal: unread,
        messagesToday,
        newPrepItemsLast24Hours: newPrepItems,
        myPrepCount: myPrep,
        loading: false,
        error: null,
        lastUpdated: now,
        nextRefreshAt: null,
      })

      return response
    } catch (error) {
      if ((error as { name?: string }).name === 'AbortError') {
        set({ loading: false })
        return null
      }

      const message = error instanceof Error && error.message ? error.message : DEFAULT_ERROR_MESSAGE

      set({
        loading: false,
        error: message,
        nextRefreshAt: null,
      })

      return null
    }
  },
  setNextRefreshAt: (timestamp) => {
    set({ nextRefreshAt: timestamp })
  },
  reset: () => {
    set(createInitialState())
  },
}))

type PollerState = {
  subscribers: number
  stationId: number | null
  timer: ReturnType<typeof setTimeout> | null
  abortController: AbortController | null
  active: boolean
}

const poller: PollerState = {
  subscribers: 0,
  stationId: null,
  timer: null,
  abortController: null,
  active: false,
}

const clearTimer = () => {
  if (poller.timer !== null) {
    clearTimeout(poller.timer)
    poller.timer = null
  }
}

const abortRequest = () => {
  if (poller.abortController) {
    poller.abortController.abort()
    poller.abortController = null
  }
}

const stopPolling = () => {
  clearTimer()
  abortRequest()
  poller.active = false
  poller.stationId = null
  useDashboardStatsStore.getState().setNextRefreshAt(null)
}

const scheduleNextFetch = (stationId: number) => {
  if (!poller.active || poller.stationId !== stationId) {
    return
  }

  const nextAt = Date.now() + LISTENER_COUNT_REFRESH_INTERVAL_MS
  useDashboardStatsStore.getState().setNextRefreshAt(nextAt)

  poller.timer = setTimeout(() => {
    poller.timer = null

    if (!poller.active || poller.stationId !== stationId) {
      return
    }

    void runPollerFetch(stationId)
  }, LISTENER_COUNT_REFRESH_INTERVAL_MS)
}

const runPollerFetch = async (stationId: number) => {
  if (!poller.active || poller.stationId !== stationId) {
    return
  }

  clearTimer()

  const controller = new AbortController()
  poller.abortController = controller

  try {
    await useDashboardStatsStore.getState().fetch(stationId, { signal: controller.signal })
  } catch (error) {
    if ((error as { name?: string }).name !== 'AbortError' && (import.meta as any)?.env?.DEV) {
      console.error('[RPA] Failed to load dashboard stats', error)
    }
  } finally {
    poller.abortController = null

    if (!poller.active || poller.stationId !== stationId) {
      return
    }

    scheduleNextFetch(stationId)
  }
}

const startPolling = (stationId: number) => {
  if (!Number.isFinite(stationId) || stationId <= 0) {
    useDashboardStatsStore.getState().reset()
    return
  }

  poller.stationId = stationId
  poller.active = true
  useDashboardStatsStore.getState().setNextRefreshAt(null)

  void runPollerFetch(stationId)
}

const isValidStationId = (stationId: number | null | undefined): stationId is number =>
  typeof stationId === 'number' && Number.isFinite(stationId) && stationId > 0

const useDashboardStatsSubscription = (stationId: number | null) => {
  useEffect(() => {
    poller.subscribers += 1

    if (!isValidStationId(stationId)) {
      useDashboardStatsStore.getState().reset()
      stopPolling()
    } else if (poller.stationId !== stationId) {
      useDashboardStatsStore.getState().reset()
      stopPolling()
      startPolling(stationId)
    } else if (!poller.active) {
      startPolling(stationId)
    }

    return () => {
      poller.subscribers = Math.max(0, poller.subscribers - 1)

      if (poller.subscribers === 0) {
        stopPolling()
        useDashboardStatsStore.getState().reset()
      }
    }
  }, [stationId])
}

const baseSelector = (state: DashboardStatsStore): DashboardStatsData => ({
  unreadTotal: state.unreadTotal,
  messagesToday: state.messagesToday,
  newPrepItemsLast24Hours: state.newPrepItemsLast24Hours,
  myPrepCount: state.myPrepCount,
  loading: state.loading,
  error: state.error,
  lastUpdated: state.lastUpdated,
  nextRefreshAt: state.nextRefreshAt,
})

export type DashboardStatsCardType =
  | 'unreadTotal'
  | 'messagesToday'
  | 'newPrepItemsLast24Hours'
  | 'myPrepCount'

export type DashboardStatsCardState = {
  value: number | null
  loading: boolean
  error: string | null
  lastUpdated: number | null
  nextRefreshAt: number | null
}

const cardValueSelectors: Record<DashboardStatsCardType, (state: DashboardStatsStore) => number | null> = {
  unreadTotal: (state) => state.unreadTotal,
  messagesToday: (state) => state.messagesToday,
  newPrepItemsLast24Hours: (state) => state.newPrepItemsLast24Hours,
  myPrepCount: (state) => state.myPrepCount,
}

export const useDashboardStats = (stationId: number | null): DashboardStatsData => {
  useDashboardStatsSubscription(stationId)
  return useDashboardStatsStore(baseSelector)
}

export const useDashboardStatsCard = (
  stationId: number | null,
  type: DashboardStatsCardType,
): DashboardStatsCardState => {
  useDashboardStatsSubscription(stationId)

  const selector = useMemo(
    () =>
      (state: DashboardStatsStore): DashboardStatsCardState => ({
        value: cardValueSelectors[type](state),
        loading: state.loading,
        error: state.error,
        lastUpdated: state.lastUpdated,
        nextRefreshAt: state.nextRefreshAt,
      }),
    [type],
  )

  return useDashboardStatsStore(selector)
}

export default useDashboardStatsStore
